import { ListingProductCode, type ListingProduct } from 'src/generated/graphql';
import type { ClientListingForListingsTab } from 'src/types/graphql-derived';

import { ConsoleErrorId, consoleErrorWrapper } from '.';

// ============================================================================
// Product level type guards
// ============================================================================

const isStandard = (product: ListingProduct) =>
  product.productCode === ListingProductCode.Standard;

export const isEmailBoostAdPack = (product: ListingProduct) =>
  product.productCode === ListingProductCode.EmailBoost;

export const isEmailBoostStandard = (product: ListingProduct) =>
  product.productCode === ListingProductCode.EmailBoostLus;

const isSearchBoostAdPack = (product: ListingProduct) =>
  product.productCode === ListingProductCode.BoostedAd;

const isSearchBoostStandard = (product: ListingProduct) =>
  product.productCode === ListingProductCode.BoostedAdLus;

// ============================================================================
// Base listing agnostic product type guards
// ============================================================================

export const isEmailBoost = (product: ListingProduct) =>
  product.productCode === ListingProductCode.EmailBoost ||
  product.productCode === ListingProductCode.EmailBoostLus;

export const isSearchBoost = (product: ListingProduct) =>
  product.productCode === ListingProductCode.BoostedAd ||
  product.productCode === ListingProductCode.BoostedAdLus;

// ============================================================================
// Combination product guards
// ============================================================================

export const isEmailAndSearchBoostAdPackCombination = (
  firstProduct: ListingProduct,
  secondProduct: ListingProduct,
) => {
  // We don't care what order the products are in, we just care that the 2 products
  // include one of each type
  const adPackCombinations = [
    [isEmailBoostAdPack, isSearchBoostAdPack],
    [isSearchBoostAdPack, isEmailBoostAdPack],
  ];

  // Do one of the above combinations match?
  return adPackCombinations.some(
    ([firstAssertion, secondAssertion]) =>
      firstAssertion(firstProduct) && secondAssertion(secondProduct),
  );
};

export const isEmailAndSearchBoostStandardCombination = (
  firstProduct: ListingProduct,
  secondProduct: ListingProduct,
) => {
  // We don't care what order the products are in, we just care that the 2 products
  // include one of each type
  const standardCombinations = [
    [isEmailBoostStandard, isSearchBoostStandard],
    [isSearchBoostStandard, isEmailBoostStandard],
  ];
  // Do one of the above combinations match?
  return standardCombinations.some(
    ([firstAssertion, secondAssertion]) =>
      firstAssertion(firstProduct) && secondAssertion(secondProduct),
  );
};

export const isStandardAndSearchBoostCombination = (
  firstProduct: ListingProduct,
  secondProduct: ListingProduct,
) => {
  // We don't care what order the products are in, we just care that the 2 products
  // include one of each type
  const combinations = [
    [isStandard, isSearchBoostStandard],
    [isSearchBoostStandard, isStandard],
  ];

  // Do one of the above combinations match?
  return combinations.some(
    ([firstAssertion, secondAssertion]) =>
      firstAssertion(firstProduct) && secondAssertion(secondProduct),
  );
};

// ============================================================================
// Other
// ============================================================================

type ValidateProductsResult =
  | { kind: 'error' }
  | {
      kind: 'success';
      singleProducts: {
        freemium: ListingProduct;
        standard: ListingProduct;
        accelerate: ListingProduct;
        searchBoostAdPack: ListingProduct;
        searchBoostStandard: ListingProduct;
        emailBoostAdPack: ListingProduct;
        emailBoostStandard: ListingProduct;
      };
      combinedProducts: {
        standardAndSearchBoost: ListingProduct;
        emailBoostAndSearchBoostAdPack: ListingProduct;
        emailBoostAndSearchBoostStandard: ListingProduct;
      };
    };

export const validateListingProducts = (
  products: ListingProduct[],
): ValidateProductsResult => {
  const freemium = products.find(
    (product) => product.productCode === ListingProductCode.Freemium,
  );

  const standard = products.find(
    (product) => product.productCode === ListingProductCode.Standard,
  );

  const accelerate = products.find(
    (product) => product.productCode === ListingProductCode.Accelerate,
  );

  const searchBoostAdPack = products.find(
    (product) => product.productCode === ListingProductCode.BoostedAd,
  );

  const searchBoostStandard = products.find(
    (product) => product.productCode === ListingProductCode.BoostedAdLus,
  );

  const emailBoostAdPack = products.find(
    (product) => product.productCode === ListingProductCode.EmailBoost,
  );

  const emailBoostStandard = products.find(
    (product) => product.productCode === ListingProductCode.EmailBoostLus,
  );

  const standardAndSearchBoost = products.find(
    (product) =>
      product.productCode === ListingProductCode.StandardAndSearchBoost,
  );

  const emailBoostAndSearchBoostAdPack = products.find(
    (product) =>
      product.productCode === ListingProductCode.EmailBoostAndSearchBoost,
  );

  const emailBoostAndSearchBoostStandard = products.find(
    (product) =>
      product.productCode === ListingProductCode.EmailBoostAndSearchBoostLus,
  );

  if (
    !freemium ||
    !standard ||
    !accelerate ||
    !searchBoostAdPack ||
    !searchBoostStandard ||
    !emailBoostAdPack ||
    !emailBoostStandard ||
    !standardAndSearchBoost ||
    !emailBoostAndSearchBoostAdPack ||
    !emailBoostAndSearchBoostStandard
  ) {
    consoleErrorWrapper(ConsoleErrorId.PaymentProductValidation, {
      freemium,
      standard,
      accelerate,
      searchBoostAdPack,
      searchBoostStandard,
      emailBoostAdPack,
      emailBoostStandard,
      standardAndSearchBoost,
      emailBoostAndSearchBoostAdPack,
      emailBoostAndSearchBoostStandard,
    });

    return {
      kind: 'error',
    };
  }

  return {
    kind: 'success',
    singleProducts: {
      freemium,
      standard,
      accelerate,
      searchBoostAdPack,
      searchBoostStandard,
      emailBoostAdPack,
      emailBoostStandard,
    },
    combinedProducts: {
      standardAndSearchBoost,
      emailBoostAndSearchBoostAdPack,
      emailBoostAndSearchBoostStandard,
    },
  };
};

// ============================================================================
// Remove below
// ============================================================================

export const findListingProductByProductCode = <
  T extends {
    productCode: ListingProductCode;
  },
>(
  listingProducts: T[],
  productCode: ListingProductCode,
): T | undefined =>
  // Change to use isListingProduct(productCode)
  listingProducts.find((x) => x.productCode === productCode);

export const getIsListingLus = <
  TListingProduct extends {
    productCode: ListingProductCode;
    legacyProductCode: string;
  },
  TListing extends {
    legacyProductCode: string | null;
  },
>(
  listingProducts: TListingProduct[],
  listing: TListing,
) => {
  const selectedProduct = listingProducts.find(
    (x) => x.legacyProductCode === listing.legacyProductCode,
  );

  const isAdPack =
    selectedProduct?.productCode === ListingProductCode.SixAdPack;
  return !isAdPack;
};

export const getSearchBoostProduct = <
  T extends {
    productCode: ListingProductCode;
    legacyProductCode: string;
  },
>(
  listingProducts: T[],
  listing: ClientListingForListingsTab,
) => {
  const selectedProduct = listingProducts.find(
    (x) => x.legacyProductCode === listing.legacyProductCode,
  );

  if (!selectedProduct) return;

  const searchBoostProduct =
    selectedProduct.productCode === ListingProductCode.SixAdPack
      ? ListingProductCode.BoostedAd
      : ListingProductCode.BoostedAdLus;
  return findListingProductByProductCode(listingProducts, searchBoostProduct);
};

export const getEmailBoostProduct = <
  T extends {
    productCode: ListingProductCode;
    legacyProductCode: string;
  },
>(
  listingProducts: T[],
  listing: ClientListingForListingsTab,
) => {
  const selectedProduct = listingProducts.find(
    (x) => x.legacyProductCode === listing.legacyProductCode,
  );

  if (!selectedProduct) return;

  const emailBoostProduct =
    selectedProduct.productCode === ListingProductCode.SixAdPack
      ? ListingProductCode.EmailBoost
      : ListingProductCode.EmailBoostLus;

  return findListingProductByProductCode(listingProducts, emailBoostProduct);
};
